import React, { useState } from 'react';
import {
  GradientDefs,
  Hint,
  HorizontalGridLines,
  VerticalBarSeries,
  VerticalBarSeriesPoint,
  VerticalGridLines,
  XAxis,
} from 'react-vis';
import 'react-vis/dist/style.css';
import ScrollableXYPlot from './ScrollableXYPlot';
import { collapseDataGaps } from './collapseDataGaps';
import { renderVoidMarkers } from './VoidMarkers';

// Fixed horizontal space allotted to each year so bars keep a consistent width and the chart grows
// (and scrolls) as more years of data are shown, rather than being squeezed into the container.
const PX_PER_YEAR = 52;
const BAR_WIDTH = 0.85;
const CHART_MARGIN = { left: 60, right: 20, top: 10, bottom: 55 };

interface ProductionChartProps {
  seriesData: any[];
  yTitle: string;
  h: number;
  color?: string;
  lastFullYear?: number;
}

const firstDigit = (x: number): number => {
  return parseInt(('' + Math.max(0, x))[0], 10);
};

const firstTwoDigits = (x: number): number => {
  return parseInt(('' + Math.max(0, x)).slice(0, 2), 10);
};

const numDigits = (x: number): number => {
  return Math.max(Math.floor(Math.log10(Math.abs(x))), 0) + 1;
};

const formatTick = (x: number): string => {
  const digits = numDigits(x);
  const divisor = digits >= 10 ? 1000000000 : digits >= 7 ? 1000000 : digits >= 4 ? 1000 : 1;
  const suffix = divisor === 1000000000 ? 'B' : divisor === 1000000 ? 'M' : divisor === 1000 ? 'K' : '';
  return (x / divisor).toString().concat(suffix);
};

const getYDomain = (x: number): number => {
  const n = numDigits(x);
  const f = firstDigit(x);
  if (n < 2 || f >= 5) return Math.max(10, Math.pow(10, Math.max(1, n - 1)) * (f + 1));
  return Math.max(10, Math.pow(10, Math.max(1, n - 2)) * (firstTwoDigits(x) + 1));
};

const ProductionChart = (props: ProductionChartProps) => {
  const { seriesData, h, yTitle, color = 'darkgrey', lastFullYear } = props;
  const [mouseValue, setMouseValue] = useState<VerticalBarSeriesPoint | undefined>(undefined);

  if (seriesData?.length < 1) {
    return <h6>No data</h6>;
  }

  const handleValueMouseOver = (value: VerticalBarSeriesPoint) => setMouseValue(value);
  const handleMouseOut = () => setMouseValue(undefined);
  const formatTickLabel = (v: number) => (lastFullYear && v > lastFullYear ? `${v}*` : v.toString());

  const exceedsLastFullYear = seriesData.some((sd) => lastFullYear && sd.x > lastFullYear);

  const hashId = `${yTitle.replace(/[^a-zA-Z0-9]/g, '')}HashId`;

  // A year is "incomplete reporting" when it is past the last fully-reported year; such years are
  // always kept (and hatched) even when their value is 0, so they are never collapsed away.
  const isIncompleteYear = (year: number) => !!lastFullYear && year > lastFullYear;

  // Collapse runs of empty years (value 0, and not incomplete-reporting) into a single labelled
  // column and remap the remaining bars onto sequential positions, so data voids — including runs at
  // the very start or end of the series — don't waste horizontal space.
  const years = seriesData.map((d: any) => d.x);
  const seriesMinYear = Math.min(...years);
  const seriesMaxYear = Math.max(...years);
  const dataPoints = seriesData.filter((d: any) => d.y > 0 || isIncompleteYear(d.x));
  // Fall back to showing everything if nothing qualifies as real data (shouldn't happen in practice,
  // as callers guard against empty series).
  const keptPoints = dataPoints.length > 0 ? dataPoints : seriesData;
  const keptYears = keptPoints.map((d: any) => d.x);

  const layout = collapseDataGaps(keptYears, { minYear: seriesMinYear, maxYear: seriesMaxYear });
  const plotData = keptPoints.map((sd: any) => {
    const point = isIncompleteYear(sd.x) ? { ...sd, color: `url(#${hashId})` } : sd;
    return { ...point, x: layout.posByYear.get(sd.x) };
  });
  const labelByPos = new Map<number, string>();
  layout.ticks.forEach((t) => {
    labelByPos.set(t.pos, t.year !== undefined ? formatTickLabel(t.year) : t.label);
  });
  const tickValues = layout.ticks.map((t) => t.pos);

  const maxY = Math.max(...keptPoints.map((d: any) => d.y));
  const plotWidth = layout.columnCount * PX_PER_YEAR;
  // Expand the domain by half a bar on each side so the first and last bars sit flush against the
  // plot edges without being clipped. When a void occupies an edge, extend the domain to the void's
  // outer edge instead so the collapsed marker sits flush.
  const xDomain = [
    layout.hasLeadingVoid ? 0 : layout.firstPos - BAR_WIDTH / 2,
    layout.hasTrailingVoid ? layout.columnCount : layout.lastPos + BAR_WIDTH / 2,
  ];
  const yDomain = [0, getYDomain(maxY)];

  return (
    <div style={{ paddingBottom: '2rem' }}>
      <ScrollableXYPlot
        height={h}
        plotWidth={plotWidth}
        margin={CHART_MARGIN}
        xDomain={xDomain}
        yDomain={yDomain}
        yTitle={yTitle}
        xTitle="Year"
        yTickFormat={formatTick}
      >
        <GradientDefs>
          <pattern id={hashId} width={5} height={5} patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
            <rect height={5} width={5} style={{ stroke: color, fill: 'white' }} />
          </pattern>
        </GradientDefs>
        <VerticalGridLines tickValues={tickValues} />
        <HorizontalGridLines />
        <XAxis tickValues={tickValues} tickFormat={(v: number) => labelByPos.get(v) ?? ''} />
        <VerticalBarSeries
          data={plotData}
          barWidth={BAR_WIDTH}
          colorType="literal"
          color={color}
          stroke={'#888'}
          onValueMouseOver={handleValueMouseOver}
          onValueMouseOut={handleMouseOut}
        />
        {renderVoidMarkers(layout.ticks, yDomain)}
        {mouseValue ? (
          <Hint value={mouseValue}>
            <div className={'rv-hint__content'}>{mouseValue.y.toLocaleString()}</div>
          </Hint>
        ) : null}
      </ScrollableXYPlot>
      {exceedsLastFullYear ? (
        <div style={{ color: '#888', fontSize: '0.7rem', textAlign: 'center' }}>* source reporting incomplete</div>
      ) : null}
    </div>
  );
};

export default ProductionChart;
